import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Tooltip,
} from "recharts";
import "./LineChartTwo.css";
import { useLanguage } from "../../../../Providers/LanguageContext";
import { useUserData } from "../../../../Providers/UserWrapper";

// Custom Tooltip component - Enhanced to show all values even when lines coincide
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // Create a map of all available dataKeys we want to show
    const allDataKeys = [
      "revenue",
      "revenueBudget",
      "expense",
      "expenseBudget",
      "profit",
    ];

    // Create a map of payload entries for quick lookup
    const payloadMap = {};
    payload.forEach((entry) => {
      payloadMap[entry.dataKey] = entry;
    });

    return (
      <div
        className="custom-tooltip-ln2"
        style={{
          backgroundColor: "white",
          padding: "12px",
          border: "1px solid #e2e8f0",
          borderRadius: "6px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        <p
          className="tooltip-label"
          style={{
            margin: "0 0 8px 0",
            fontWeight: "600",
            color: "#1b2b3a",
            fontSize: "14px",
          }}
        >
          {label}
        </p>
        {allDataKeys.map((dataKey, index) => {
          // Map dataKey to readable labels
          let labelName = "";
          let color = "";

          switch (dataKey) {
            case "revenue":
              labelName = "Revenue";
              color = "#a3a3a3";
              break;
            case "revenueBudget":
              labelName = "Revenue Budget";
              color = "#0f172a";
              break;
            case "expense":
              labelName = "Expense";
              color = "#334155";
              break;
            case "expenseBudget":
              labelName = "Expense Budget";
              color = "#71717a";
              break;
            case "profit":
              labelName = "Profit";
              color = "#16a34a";
              break;
            default:
              labelName = dataKey;
              color = "#64748b";
          }

          // Get the value from payload map or use 0 as default
          const entry = payloadMap[dataKey];
          const value = entry?.value || 0;

          return (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "4px",
                fontSize: "13px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: color,
                    marginRight: "8px",
                  }}
                />
                <span style={{ color: "#64748b" }}>{labelName}:</span>
              </div>
              <span
                style={{
                  fontWeight: "600",
                  color: "#1b2b3a",
                  marginLeft: "16px",
                }}
              >
                ${value?.toLocaleString() || "0"}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

// Helper function to format large numbers with K/M suffixes
const formatLargeNumber = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  } else {
    return `$${value}`;
  }
};

// Custom Calendar Component for Day Selection
const SimpleDatePicker = ({ value, onChange }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value || new Date());
  const calendarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const handleDateClick = (day) => {
    const newDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      day
    );
    setSelectedDate(newDate);
    onChange(formatDate(newDate)); // Send formatted date string to parent
    setShowCalendar(false);
  };

  const changeMonth = (increment) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setSelectedDate(newDate);
  };

  const changeYear = (increment) => {
    const newDate = new Date(selectedDate);
    newDate.setFullYear(newDate.getFullYear() + increment);
    setSelectedDate(newDate);
  };

  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return (
    <div
      className="simple-datepicker"
      ref={calendarRef}
      style={{ position: "relative" }}
    >
      <input
        type="text"
        value={formatDateDisplay(selectedDate)}
        readOnly
        onClick={() => setShowCalendar(!showCalendar)}
        style={{
          padding: "8px 12px",
          borderRadius: "6px",
          border: "1px solid #e2e8f0",
          backgroundColor: "white",
          fontSize: "14px",
          color: "#1b2b3a",
          width: "120px",
          cursor: "pointer",
        }}
      />
      {showCalendar && (
        <div
          className="calendar-popup"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            padding: "12px",
            zIndex: 1000,
            width: "280px",
            marginTop: "4px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <button
              onClick={() => changeYear(-1)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              &lt;&lt;
            </button>
            <button
              onClick={() => changeMonth(-1)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              &lt;
            </button>
            <div style={{ fontWeight: "600" }}>
              {monthNames[month]} {year}
            </div>
            <button
              onClick={() => changeMonth(1)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              &gt;
            </button>
            <button
              onClick={() => changeYear(1)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              &gt;&gt;
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
              marginBottom: "8px",
            }}
          >
            {dayNames.map((day) => (
              <div
                key={day}
                style={{
                  textAlign: "center",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#64748b",
                }}
              >
                {day}
              </div>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "4px",
            }}
          >
            {days.map((day, index) => (
              <div key={index} style={{ textAlign: "center" }}>
                {day ? (
                  <button
                    onClick={() => handleDateClick(day)}
                    style={{
                      width: "32px",
                      height: "32px",
                      border: "none",
                      background:
                        day === selectedDate.getDate()
                          ? "#3b82f6"
                          : "transparent",
                      color:
                        day === selectedDate.getDate() ? "white" : "#1b2b3a",
                      borderRadius: "50%",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  >
                    {day}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Year Picker Component
const YearPicker = ({ value, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [selectedYear, setSelectedYear] = useState(
    value || new Date().getFullYear()
  );
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    onChange(year.toString()); // Send year as string to parent
    setShowPicker(false);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear - 10 + i);

  return (
    <div
      className="year-picker"
      ref={pickerRef}
      style={{ position: "relative" }}
    >
      <input
        type="text"
        value={selectedYear}
        readOnly
        onClick={() => setShowPicker(!showPicker)}
        style={{
          padding: "8px 12px",
          borderRadius: "6px",
          border: "1px solid #e2e8f0",
          backgroundColor: "white",
          fontSize: "14px",
          color: "#1b2b3a",
          width: "100px",
          cursor: "pointer",
        }}
      />
      {showPicker && (
        <div
          className="year-picker-popup"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            padding: "12px",
            zIndex: 1000,
            width: "200px",
            maxHeight: "300px",
            overflowY: "auto",
            marginTop: "4px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "8px",
            }}
          >
            {years.map((year) => (
              <button
                key={year}
                onClick={() => handleYearSelect(year)}
                style={{
                  padding: "8px",
                  border: "none",
                  background: year === selectedYear ? "#3b82f6" : "transparent",
                  color: year === selectedYear ? "white" : "#1b2b3a",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LineChartTwo = ({
  data,
  time,
  setSelectedTwo,
  selectedYearFromDashboardPage,
}) => {
  const { language } = useLanguage();
  const { isBusiness } = useUserData();

  // Initialize states as empty strings
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  // Clear other selections when time changes
  useEffect(() => {
    setSelectedDay("");
    setSelectedWeek("");
    setSelectedMonth("");
    setSelectedYear("");
    // Clear parent selection when time changes
    setSelectedTwo({ day: "", week: "", month: "", year: "" });
  }, [time]);

  // Update parent's selectedTwo whenever any selection changes
  useEffect(() => {
    if (selectedDay) {
      setSelectedTwo((prev) => ({
        day: selectedDay,
        week: "",
        month: "",
        year: "",
      }));
    }
  }, [selectedDay]);

  useEffect(() => {
    if (selectedWeek) {
      setSelectedTwo((prev) => ({
        day: "",
        week: selectedWeek,
        month: "",
        year: "",
      }));
    }
  }, [selectedWeek]);

  useEffect(() => {
    if (selectedMonth) {
      setSelectedTwo((prev) => ({
        day: "",
        week: "",
        month: selectedMonth,
        year: "",
      }));
    }
  }, [selectedMonth]);

  useEffect(() => {
    if (selectedYear) {
      setSelectedTwo((prev) => ({
        day: "",
        week: "",
        month: "",
        year: selectedYear,
      }));
    }
  }, [selectedYear]);

  const [text, setText] = useState(() => {
    if (time === "day") {
      return language.two_week;
    } else if (time === "week") {
      return language.two_week_total;
    } else if (time === "month") {
      return isBusiness
        ? language.curr_month_act_budget
        : language.current_month;
    } else if (time === "year") {
      return isBusiness ? language.curr_year_act_budget : language.current_year;
    }
    return "";
  });

  useEffect(() => {
    if (time === "day") {
      setText(language.two_week);
    } else if (time === "week") {
      setText(language.two_week_total);
    } else if (time === "month") {
      setText(
        isBusiness ? language.curr_month_act_budget : language.current_month
      );
    } else if (time === "year") {
      setText(
        isBusiness ? language.curr_year_act_budget : language.current_year
      );
    }
  }, [time, language, isBusiness]);

  const apiData = data || [];

  // Month options for dropdown
  const monthOptions = [
    { month: "Jan", value: "1" },
    { month: "Feb", value: "2" },
    { month: "Mar", value: "3" },
    { month: "Apr", value: "4" },
    { month: "May", value: "5" },
    { month: "Jun", value: "6" },
    { month: "Jul", value: "7" },
    { month: "Aug", value: "8" },
    { month: "Sep", value: "9" },
    { month: "Oct", value: "10" },
    { month: "Nov", value: "11" },
    { month: "Dec", value: "12" },
  ];

  // Generate week options (1 to 52)
  const weekOptions = Array.from({ length: 52 }, (_, i) => ({
    label: `Week ${i + 1}`,
    value: `${i + 1}`,
  }));

  // 1) Normalize API data -> shape Recharts expects
  const chartData = useMemo(() => {
    console.log("API DATA", apiData);
    if (!apiData || apiData.length === 0) return [];

    const first = apiData[0];
    if (
      (first.month &&
        (first.revenue !== undefined || first.expense !== undefined)) ||
      (!first.label && !first.type && !first.value)
    ) {
      return apiData;
    }

    const grouped = apiData.reduce((acc, item) => {
      const { label, value, type } = item;
      if (!label || typeof value !== "number" || !type) return acc;

      if (!acc[label]) {
        acc[label] = { month: label };
      }

      acc[label][type] = value;

      if (type === "revenue") {
        acc[label].revenueLabel = `$${value.toLocaleString()}`;
      }

      return acc;
    }, {});

    return Object.values(grouped);
  }, [apiData]);

  // 2) Dynamic Y‑axis: 0 -> max value across all series
  const yDomain = useMemo(() => {
    if (!chartData.length) return [0, 1];

    let max = 0;
    chartData.forEach((point) => {
      [
        "revenue",
        "revenueBudget",
        "expense",
        "expenseBudget",
        "profit",
      ].forEach((key) => {
        if (typeof point[key] === "number" && point[key] > max) {
          max = point[key];
        }
      });
    });

    if (max === 0) return [0, 1];
    return [0, max];
  }, [chartData]);

  // Function to generate Y-axis ticks based on max value
  const generateYTicks = () => {
    const max = yDomain[1];

    if (max <= 1) return [0, 0.2, 0.4, 0.6, 0.8, 1];

    // For larger values, create 5-6 nice ticks
    const tickCount = 6;
    const niceMax =
      Math.ceil(max / Math.pow(10, Math.floor(Math.log10(max)))) *
      Math.pow(10, Math.floor(Math.log10(max)));
    const tickStep = niceMax / (tickCount - 1);

    const ticks = [];
    for (let i = 0; i < tickCount; i++) {
      ticks.push(Math.round(i * tickStep));
    }

    return ticks;
  };

  // Custom Y-axis tick formatter that uses K/M suffixes
  const yAxisTickFormatter = (value) => {
    return formatLargeNumber(value);
  };

  // Render appropriate selector based on time prop
  const renderSelector = () => {
    switch (time) {
      case "day":
        return (
          <div
            className="date-selector-container"
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 10,
            }}
          >
            <SimpleDatePicker
              value={selectedDay ? new Date(selectedDay) : new Date()}
              onChange={(dateString) => setSelectedDay(dateString)}
            />
          </div>
        );

      case "week":
        return (
          <div
            className="week-selector-container"
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 10,
            }}
          >
            <select
              value={selectedWeek}
              onChange={(e) => {
                setSelectedWeek(e.target.value);
              }}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                backgroundColor: "white",
                fontSize: "14px",
                color: "#1b2b3a",
                cursor: "pointer",
              }}
            >
              <option value="">Select Week</option>
              {weekOptions.map((week) => (
                <option key={week.value} value={week.value}>
                  {week.label}
                </option>
              ))}
            </select>
          </div>
        );

      case "month":
        return (
          <div
            className="month-selector-container"
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 10,
            }}
          >
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
              }}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                backgroundColor: "white",
                fontSize: "14px",
                color: "#1b2b3a",
                cursor: "pointer",
              }}
            >
              <option value="">Select Month</option>
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.month}
                </option>
              ))}
            </select>
          </div>
        );

      // case "year":
      //   return (
      //     <div
      //       className="year-selector-container"
      //       style={{
      //         position: "absolute",
      //         top: "20px",
      //         right: "20px",
      //         zIndex: 10,
      //       }}
      //     >
      //       <YearPicker
      //         value={
      //           selectedYear ? parseInt(selectedYear) : new Date().getFullYear()
      //         }
      //         onChange={(yearString) => setSelectedYear(yearString)}
      //       />
      //     </div>
      //   );

      default:
        return null;
    }
  };

  return (
    <div className="chart-card-ln2" style={{ position: "relative" }}>
      {/* Add the selector in the top right corner */}
      {renderSelector()}

      <h2
        className="chart-title-ln2"
        style={{ paddingRight: time ? "140px" : "20px" }}
      >
        {text}
      </h2>

      <div className="chart-wrapper-ln2">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 25 }} // Increased bottom margin
          >
            <CartesianGrid
              vertical={false}
              stroke="#94a3b8"
              strokeDasharray="4 4"
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{
                stroke: "#cbd5e1",
                strokeWidth: 1,
                strokeDasharray: "3 3",
              }}
              shared={false}
            />

            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#1b2b3a" }}
              tickFormatter={(value) => value}
              interval={0} // Force all labels to show
              padding={{ left: 10, right: 10 }} // Add padding
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#1b2b3a" }}
              domain={yDomain}
              allowDecimals={false}
              tickFormatter={yAxisTickFormatter}
              ticks={generateYTicks()}
            />

            <Line
              type="monotone"
              dataKey="revenueBudget"
              stroke="#0f172a"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={(props) => {
                const { cx, cy, index } = props;
                const visibleDots = [0, 3, 4, 7];

                if (visibleDots.includes(index)) {
                  return <circle cx={cx} cy={cy} r={5} fill="#0f172a" />;
                }
                return null;
              }}
              name="Revenue Budget"
            />

            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#a3a3a3"
              strokeWidth={3}
              dot={(props) => {
                const { cx, cy, index } = props;
                const visibleDots = [0, 3, 4, 7];

                if (visibleDots.includes(index)) {
                  return <circle cx={cx} cy={cy} r={7} fill="#a3a3a3" />;
                }
                return null;
              }}
              name="Revenue"
            >
              <LabelList
                dataKey="revenueLabel"
                position="right"
                className="value-label-ln2"
              />
            </Line>

            <Line
              type="monotone"
              dataKey="expenseBudget"
              stroke="#71717a"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={(props) => {
                const { cx, cy, index } = props;
                const visibleDots = [0, 3, 4, 7];

                if (visibleDots.includes(index)) {
                  return <circle cx={cx} cy={cy} r={5} fill="#71717a" />;
                }
                return null;
              }}
              name="Expense Budget"
            />

            <Line
              type="monotone"
              dataKey="expense"
              stroke="#334155"
              strokeWidth={3}
              dot={(props) => {
                const { cx, cy, index } = props;
                const visibleDots = [0, 3, 4, 7];

                if (visibleDots.includes(index)) {
                  return <circle cx={cx} cy={cy} r={7} fill="#334155" />;
                }
                return null;
              }}
              name="Expense"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-legend-ln2">
        <div>
          <span className="dot revenue-ln2"></span> Revenue
        </div>
        <div>
          <span className="dot revenueBudget-ln2"></span> Revenue Budget
        </div>
        <div>
          <span className="dot expense-ln2"></span> Expense
        </div>
        <div>
          <span className="dot expenseBudget-ln2"></span> Expense Budget
        </div>
      </div>
    </div>
  );
};

export default LineChartTwo;
